import { getApiEndpoint } from "../config/apiEndpoints";
import { fetchWithAuth } from "../utils/tokenRefresh";
import { Goal, GoalResponse } from "../types";

const DAYS_ENDPOINT = getApiEndpoint("/days");

async function apiCall<T>(body: Record<string, unknown>): Promise<T> {
  const response = await fetchWithAuth(DAYS_ENDPOINT, {
    method: "POST",
    body: JSON.stringify(body),
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || `HTTP ${response.status}`);
  }
  return data;
}

export async function getGoals(): Promise<Goal[]> {
  const data = await apiCall<{ goals: Goal[] }>({ request_type: "get_goals" });
  return data.goals;
}

export async function createGoal(goal: {
  title: string;
  description?: string;
  goal_type: "binary" | "numeric";
  target?: number;
  frequency_days?: number;
  start_date?: string;
  end_date?: string;
}): Promise<Goal> {
  const data = await apiCall<{ goal: Goal }>({ request_type: "create_goal", ...goal });
  return data.goal;
}

export async function updateGoal(goalUuid: string, updates: Partial<Goal>): Promise<Goal> {
  const data = await apiCall<{ goal: Goal }>({
    request_type: "update_goal",
    goal_uuid: goalUuid,
    ...updates,
  });
  return data.goal;
}

export async function deleteGoal(goalUuid: string): Promise<void> {
  await apiCall({ request_type: "delete_goal", goal_uuid: goalUuid });
}

export async function getResponsesForDate(date: string): Promise<GoalResponse[]> {
  const data = await apiCall<{ responses: GoalResponse[] }>({
    request_type: "get_responses_for_date",
    date,
  });
  return data.responses;
}

export async function getResponsesForRange(startDate: string, endDate: string): Promise<GoalResponse[]> {
  const data = await apiCall<{ responses: GoalResponse[] }>({
    request_type: "get_responses_for_range",
    start_date: startDate,
    end_date: endDate,
  });
  return data.responses;
}

export async function saveResponse(goalUuid: string, date: string, value: boolean | number, notes?: string): Promise<GoalResponse> {
  const data = await apiCall<{ response: GoalResponse }>({
    request_type: "save_response",
    goal_uuid: goalUuid,
    date,
    value,
    notes: notes || "",
  });
  return data.response;
}

export async function getGoalHistory(goalUuid: string, startDate?: string, endDate?: string): Promise<{ goal: Goal; responses: GoalResponse[] }> {
  return apiCall<{ goal: Goal; responses: GoalResponse[] }>({
    request_type: "get_goal_history",
    goal_uuid: goalUuid,
    start_date: startDate,
    end_date: endDate,
  });
}
